import { Component, Inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  AssignUserTagRequest, CreateUserRequest, GetUserByIdRequest,
  IdentityWebApiClient, IUser, Role
} from "identity-webapi-client";
import { AuthService, StateService } from "src/app/services";
import { AspNetEmailValidationRegex } from "src/app/shared/constants/email-validation";
import { SituBaseComponent } from "src/app/shared/situ-base-component";

@Component({
  selector: 'user-copy-dialog',
  templateUrl: './user-copy-dialog.component.html',
  styleUrls: ['./user-copy-dialog.component.scss']
})
export class UserCopyDialogComponent extends SituBaseComponent {
  sourceUser: IUser | null = null;
  createdUser: IUser | null = null;
  copyForm: FormGroup;
  generatedPassword: string = '';
  loading: boolean = false;
  saving: boolean = false;
  tagsCopied: number = 0;
  tagsFailed: number = 0;

  get isCreated(): boolean { return !!this.createdUser; }
  get sourceRoleNames(): string { return this.sourceUser?.roles?.map(x => x.name).join(', ') || 'None'; }
  get sourceTagCount(): number { return this.sourceUser?.tagIds?.length ?? 0; }

  constructor(
    authService: AuthService,
    stateService: StateService,
    public dialogRef: MatDialogRef<UserCopyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IUserCopyDialogData,
    private identityWebApiClient: IdentityWebApiClient,
    private snackBar: MatSnackBar,
    private fb: FormBuilder) {
      super(authService, stateService);
      this.copyForm = this.fb.group({
        email: ['', [Validators.required, Validators.pattern(AspNetEmailValidationRegex)]],
        firstName: [''],
        lastName: ['']
      });
    }

  ngOnInit(): void {
    super.ngOnInit();
    this.generatedPassword = this.generatePassword();
    this.loadSourceUser();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onDone() {
    this.dialogRef.close({ user: this.createdUser, open: false } as IUserCopyDialogResult);
  }

  onOpenNewUser() {
    this.dialogRef.close({ user: this.createdUser, open: true } as IUserCopyDialogResult);
  }

  onCreate() {
    if (this.copyForm.invalid || !this.sourceUser || this.saving) {
      return;
    }

    this.saving = true;
    const request = new CreateUserRequest();
    // Everything except the identity fields is carried over from the source user.
    request.companyId = this.sourceUser.companyId;
    // Must be real Role instances - CreateUserRequest.toJSON() calls toJSON() on each role.
    request.roles = this.sourceUser.roles?.map(x => new Role({ id: x.id, name: x.name }));
    request.isEnabled = this.sourceUser.isEnabled;
    request.lockoutEnabled = false;
    request.position = this.sourceUser.position;
    request.email = this.copyForm.value.email?.trim();
    request.firstName = this.copyForm.value.firstName?.trim();
    request.lastName = this.copyForm.value.lastName?.trim();
    request.password = this.generatedPassword;

    const unableToCopyUserMessage = "Unable to copy user";
    this.identityWebApiClient.createUser(request)
      .then(response => {
        if (response?.success && response.user) {
          const newUser = response.user;
          return this.copyTags(newUser.id).then(() => {
            this.createdUser = newUser;
            this.saving = false;
          });
        }

        this.showMessage(response?.errorMessages?.length ? response.errorMessages[0] : unableToCopyUserMessage);
        this.saving = false;
        return;
      })
      .catch(() => {
        this.showMessage(unableToCopyUserMessage);
        this.saving = false;
      });
  }

  copyPassword() {
    this.copyToClipboard(this.generatedPassword, 'Password copied to clipboard');
  }

  copyEmail() {
    this.copyToClipboard(this.createdUser?.email ?? '', 'Email copied to clipboard');
  }

  copyLoginDetails() {
    const details = 'Username: ' + this.createdUser?.email + '\nPassword: ' + this.generatedPassword;
    this.copyToClipboard(details, 'Login details copied to clipboard');
  }

  private copyTags(newUserId: string | undefined): Promise<void> {
    const tagIds = this.sourceUser?.tagIds ?? [];
    if (!newUserId || tagIds.length === 0) {
      return Promise.resolve();
    }

    // Tags are a separate association, so they have to be assigned one at a time after creation.
    return tagIds.reduce((chain, tagId) => chain.then(() => {
      const request = new AssignUserTagRequest();
      request.companyId = this.sourceUser?.companyId;
      request.tagId = tagId;
      request.userId = newUserId;

      return this.identityWebApiClient.assignUserTag(request)
        .then(response => {
          if (response?.success) {
            this.tagsCopied++;
          } else {
            this.tagsFailed++;
          }
        })
        .catch(() => { this.tagsFailed++; });
    }), Promise.resolve());
  }

  private loadSourceUser() {
    this.loading = true;
    const request = new GetUserByIdRequest();
    request.id = this.data.userId;

    const unableToLoadUserMessage = "Unable to load the user being copied";
    this.identityWebApiClient.getUserById(request)
      .then(response => {
        if (response?.success) {
          this.sourceUser = response.user ?? null;
        } else {
          this.showMessage(unableToLoadUserMessage);
          this.dialogRef.close();
        }
        this.loading = false;
      })
      .catch(() => {
        this.showMessage(unableToLoadUserMessage);
        this.loading = false;
        this.dialogRef.close();
      });
  }

  // Passwords must be at least 8 characters with at least one number, and the identity
  // server only accepts alphanumerics (AtLeast8CharsAndAtLeastOneNumberRegex).
  private generatePassword(length: number = 12): string {
    const letters = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ'; // no l, I, O
    const digits = '23456789'; // no 0, 1
    const all = letters + digits;
    const pick = (source: string) => source[Math.floor(Math.random() * source.length)];

    let password = [pick(letters), pick(digits), pick(digits)];
    while (password.length < length) {
      password.push(pick(all));
    }

    // Shuffle so the guaranteed characters are not always in the same position.
    for (let i = password.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join('');
  }

  private copyToClipboard(text: string, successMessage: string) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => this.showMessage(successMessage))
        .catch(() => this.fallbackCopyTextToClipboard(text, successMessage));
    } else {
      this.fallbackCopyTextToClipboard(text, successMessage);
    }
  }

  private fallbackCopyTextToClipboard(text: string, successMessage: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showMessage(successMessage);
    } catch {
      this.showMessage('Unable to copy - please select the text manually');
    }

    document.body.removeChild(textArea);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}

export interface IUserCopyDialogData {
  userId: string;
  userName: string;
}

export interface IUserCopyDialogResult {
  user: IUser | null;
  open: boolean;
}
